<script setup lang="ts">
import type { Gap } from "@kaleidoscope-solutions/engine-wasm"
import type { CellData, TableData } from "@/components/FormulaPlayground/PlaygroundUtils"
import { useVirtualList } from "@vueuse/core"
import { computed, toRef } from "vue"
import { setCellClassByCell } from "@/utils/table"

interface Props {
  tableData: TableData
  readOnly?: boolean
  nested?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: true,
})

const tableData = toRef(props, "tableData")

const columnsCount = computed(() => tableData.value[0]?.length || 0)
const rowsCount = computed(() => tableData.value.length || 0)

const rowHeight = 34 // 32 + 2 (border)

function calculateRowHeight(rowData: CellData[]): number {
  return rowHeight * rowData.reduce((acc: number, cellData: CellData) => {
    if (Array.isArray(cellData)) {
      // cellData is TableData, so use its length for height
      const nestedHeight = cellData.length * rowHeight
      if (nestedHeight > acc) {
        acc = nestedHeight
      }
    }
    return acc
  }, 1)
}

const rowHeights = computed(() => tableData.value.map((rowData) => calculateRowHeight(rowData)))

const { containerProps, wrapperProps } = useVirtualList(tableData, {
  itemHeight: (row) => rowHeights.value[row],
  overscan: 1000
})

function getCellClasses(rowIndex: number, cellIndex: number) {
  const hierarchyLevel: number = 0
  const nextRowNested: boolean = false
  const firstRow: boolean = rowIndex === 0
  const lastRow: boolean = rowIndex === rowsCount.value - 1
  const firstCellInRow: boolean = cellIndex === 0
  const lastCellInRow: boolean = cellIndex === columnsCount.value - 1
  const adjacentCellGaps: Gap[] = []

  return setCellClassByCell({
    hierarchyLevel,
    nextRowNested,
    firstRow,
    lastRow,
    firstCellInRow,
    lastCellInRow,
    adjacentCellGaps
  })
}
</script>

<template>
  <div
    v-if="tableData.length"
    v-bind="containerProps"
  >
    <div
      v-bind="wrapperProps"
      class="inline-grid text-sm"
      :style="{ gridTemplateColumns: `repeat(${columnsCount}, max-content)`, height: '100%' }"
    >
      <div
        v-if="tableData[0]"
        class="contents"
      >
        <div
          v-for="(header, colIndex) in tableData[0]"
          :key="`header-${colIndex}`"
          class="cell--ColumnHeader group/cell flex items-center justify-between gap-2 px-2 py-1"
        >
          <slot
            name="header-cell"
            :header="header"
            :col-index="colIndex"
          >
            <span class="font-bold">{{ header }}</span>
          </slot>
        </div>
      </div>
      <template
        v-for="(row, rowIndex) in tableData.slice(1)"
        :key="rowIndex + 1"
      >
        <div
          v-for="(cell, cellIndex) in row"
          :key="cellIndex"
          class="cell--Cell group/cell"
          :class="[{
            'cell--readOnly': readOnly,
          }, getCellClasses(rowIndex + 1, cellIndex),
          ]"
        >
          <div class="cell-inner">
            <slot :cell="cell">
              <template v-if="Array.isArray(cell)">
                <ArrayTable
                  :nested="true"
                  :table-data="(cell as TableData)"
                  :read-only="false"
                />
              </template>
              <template v-else>
                {{ cell }}
              </template>
            </slot>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
